/**
 * LoadingProgress Component
 * 
 * 全局載入進度指示器
 * 顯示應用程式初始化、字典下載等進度
 */

import React, { memo } from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  Paper,
  Stack,
  CircularProgress,
  Fade,
} from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useDictionary } from '../../contexts/DictionaryContext';

const LoadingProgress = memo(function LoadingProgress() {
  const { language } = useLanguage();
  const { isLoading, dataSource, loadingProgress } = useDictionary();
  const isTC = language === 'tc';

  // 如果不在載入中，不顯示
  if (!isLoading) {
    return null;
  }

  // 根據進度信息或數據源類型顯示不同的訊息
  const getLoadingMessage = () => {
    if (loadingProgress?.message) {
      // 翻譯進度訊息
      const messageMap = {
        'Checking database version...': isTC ? '正在檢查資料庫版本...' : 'Checking database version...',
        'Loading local data...': isTC ? '正在載入本地資料...' : 'Loading local data...',
        'Downloading database data...': isTC ? '正在下載資料庫資料...' : 'Downloading database data...',
        'Loading complete': isTC ? '載入完成' : 'Loading complete',
      };
      return messageMap[loadingProgress.message] || loadingProgress.message;
    }
    if (dataSource?.type === 'supabase') {
      return isTC ? '正在從資料庫下載資料...' : 'Downloading data from database...';
    } else if (dataSource?.type === 'local') {
      return isTC ? '正在載入本地資料...' : 'Loading local data...';
    }
    return isTC ? '正在初始化應用程式...' : 'Initializing application...';
  };

  // 獲取進度百分比
  const progressValue = loadingProgress?.progress || 0;

  return (
    <Fade in={isLoading} timeout={300}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(4px)',
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 4,
            borderRadius: 3,
            minWidth: { xs: 280, sm: 320 },
            maxWidth: { xs: '90%', sm: 400 },
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
          }}
        >
          <Stack spacing={3} alignItems="center">
            {/* 載入動畫 */}
            <CircularProgress 
              size={60} 
              thickness={4}
              sx={{
                color: 'primary.main',
              }}
            />

            {/* 載入訊息 */}
            <Box sx={{ width: '100%' }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: 'text.primary',
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                }}
              >
                {isTC ? '載入中' : 'Loading'}
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mb: 2,
                  fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                }}
              >
                {getLoadingMessage()}
              </Typography>

              {/* 進度條 */}
              <Box sx={{ width: '100%', position: 'relative' }}>
                <LinearProgress
                  variant={progressValue > 0 ? 'determinate' : 'indeterminate'}
                  value={progressValue}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(0, 93, 160, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      background: progressValue === 100
                        ? 'linear-gradient(90deg, #1a9431 0%, #66bb6a 100%)'
                        : 'linear-gradient(90deg, #005da0 0%, #0071a4 50%, #005da0 100%)',
                      backgroundSize: progressValue === 100 ? '100% 100%' : '200% 100%',
                      animation: progressValue === 100 ? 'none' : 'shimmer 2s infinite linear',
                    },
                    '@keyframes shimmer': {
                      '0%': {
                        backgroundPosition: '200% 0',
                      },
                      '100%': {
                        backgroundPosition: '-200% 0',
                      },
                    },
                  }}
                />
                {progressValue > 0 && progressValue < 100 && (
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: -20,
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      fontWeight: 500,
                    }}
                  >
                    {Math.round(progressValue)}%
                  </Typography>
                )}
              </Box>
            </Box>

            {/* 數據源提示 */}
            {dataSource && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  opacity: 0.7,
                }}
              >
                {dataSource.type === 'supabase' 
                  ? (isTC ? '資料來源：線上資料庫' : 'Data source: Online database')
                  : (isTC ? '資料來源：本地快取' : 'Data source: Local cache')}
              </Typography>
            )}
          </Stack>
        </Paper>
      </Box>
    </Fade>
  );
});

export default LoadingProgress;

